import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Badge,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Timeline as TimelineIcon,
  Send as SendIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  AssignmentLate as AssignmentLateIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import leadSubmissionsService from '../services/leadSubmissionsService';
import leadTrackingService from '../services/leadTrackingService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    provider: '',
    status: '',
    feedback_status: ''
  });
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Map frontend filter names to backend API parameter names
      const apiParams = {
        date_from: filters.startDate || undefined,
        date_to: filters.endDate || undefined,
        provider_ids: filters.provider ? filters.provider : undefined,
        status: filters.status || undefined,
        feedback_status: filters.feedback_status || undefined
      };

      // Remove undefined values
      Object.keys(apiParams).forEach(key => 
        apiParams[key] === undefined && delete apiParams[key]
      );

      // Fetch dashboard analytics
      const analyticsResponse = await leadSubmissionsService.getDashboardAnalytics(apiParams);
      
      // Fetch providers for filters
      const providersResponse = await leadSubmissionsService.getServiceProviders();
      setProviders(providersResponse.data || []);

      setDashboardData({
        analytics: analyticsResponse.data || {},
        providers: providersResponse.data || []
      });

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getFeedbackStatusColor = (status) => {
    switch (status) {
      case 'qualified': return 'success';
      case 'converted': return 'info';
      case 'rejected':
      case 'invalid': return 'error';
      case 'contacted': return 'info';
      case 'duplicate': return 'warning';
      case 'opened': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      case 'sent': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
          <Button onClick={fetchDashboardData} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  const analytics = dashboardData?.analytics || {};
  const providersData = dashboardData?.providers || [];

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box mb={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Agent Dashboard
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Lead Submission Tracking & Performance Insights
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboardData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={filters.provider}
                label="Provider"
                onChange={(e) => handleFilterChange('provider', e.target.value)}
              >
                <MenuItem value="">All Providers</MenuItem>
                {providers.map(provider => (
                  <MenuItem key={provider.id} value={provider.id}>{provider.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Feedback Status</InputLabel>
              <Select
                value={filters.feedback_status}
                label="Feedback Status"
                onChange={(e) => handleFilterChange('feedback_status', e.target.value)}
              >
                <MenuItem value="">All Feedback</MenuItem>
                <MenuItem value="opened">Opened</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Submissions
                  </Typography>
                  <Typography variant="h4">
                    {analytics.overall?.total || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AssignmentIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Successful
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {analytics.overall?.successful || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.overall?.total > 0 
                      ? `${Math.round((analytics.overall?.successful / analytics.overall?.total) * 100)}% success rate`
                      : '0% success rate'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircleIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Quotes Generated
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {analytics.overall?.quotes || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.overall?.successful > 0 
                      ? `${Math.round((analytics.overall?.quotes / analytics.overall?.successful) * 100)}% quote rate`
                      : '0% quote rate'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <AssignmentTurnedInIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Sales Converted
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {analytics.overall?.sales || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.overall?.quotes > 0 
                      ? `${Math.round((analytics.overall?.sales / analytics.overall?.quotes) * 100)}% conversion rate`
                      : '0% conversion rate'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    OPENED Leads
                  </Typography>
                  <Typography variant="h4" color="default.main">
                    {analytics.overall?.opened || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {analytics.overall?.total > 0 
                      ? `${Math.round((analytics.overall?.opened / analytics.overall?.total) * 100)}% opened rate`
                      : '0% opened rate'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'default.main' }}>
                  <AssignmentLateIcon />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Overview" icon={<AssessmentIcon />} />
          <Tab label="Provider Performance" icon={<SpeedIcon />} />
          <Tab label="Trends" icon={<TimelineIcon />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Provider Performance Comparison */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Provider Performance Comparison
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.providers || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="provider.name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="stats.total" fill="#8884d8" name="Total Submissions" />
                  <Bar dataKey="stats.successful" fill="#82ca9d" name="Successful" />
                  <Bar dataKey="stats.quotes" fill="#ffc658" name="Quotes" />
                  <Bar dataKey="stats.sales" fill="#ff7300" name="Sales" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          
          {/* Success Rate by Provider */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Success Rate by Provider
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={(analytics.providers || []).map(p => ({
                      name: p?.provider?.name || 'Unknown',
                      value: parseFloat(p?.stats?.successRate || 0)
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({name, value}) => `${name}: ${value}%`}
                  >
                    {(analytics.providers || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          
          {/* Detailed Provider Statistics */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detailed Provider Statistics
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Success Rate</TableCell>
                      <TableCell align="right">OPENED</TableCell>
                      <TableCell align="right">Quotes</TableCell>
                      <TableCell align="right">Quote Rate</TableCell>
                      <TableCell align="right">Sales</TableCell>
                      <TableCell align="right">Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analytics.providers || []).map((provider) => (
                      <TableRow key={provider?.provider?.id || Math.random()}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {provider?.provider?.name || 'Unknown Provider'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {provider?.provider?.api_type || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{provider?.stats?.total || 0}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${provider?.stats?.successRate || 0}%`}
                            color={parseFloat(provider?.stats?.successRate || 0) > 80 ? 'success' : 
                                   parseFloat(provider?.stats?.successRate || 0) > 60 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{provider?.stats?.opened || 0}</TableCell>
                        <TableCell align="right">{provider?.stats?.quotes || 0}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${provider?.stats?.leadToQuoteRatio || 0}%`}
                            color={parseFloat(provider?.stats?.leadToQuoteRatio || 0) >= 25 ? 'success' : 
                                   parseFloat(provider?.stats?.leadToQuoteRatio || 0) >= 15 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{provider?.stats?.sales || 0}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${provider?.stats?.conversionRate || 0}%`}
                            color={parseFloat(provider?.stats?.conversionRate || 0) > 20 ? 'success' : 
                                   parseFloat(provider?.stats?.conversionRate || 0) > 10 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Grid container spacing={3}>
          {/* Performance by Provider */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Performance by Provider
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell align="right">Total Submissions</TableCell>
                      <TableCell align="right">Successful</TableCell>
                      <TableCell align="right">Success Rate</TableCell>
                      <TableCell align="right">Quotes</TableCell>
                      <TableCell align="right">Quote Rate</TableCell>
                      <TableCell align="right">Sales</TableCell>
                      <TableCell align="right">Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analytics.providers || []).map((provider) => (
                      <TableRow key={provider?.provider?.id}>
                        <TableCell>{provider?.provider?.name || 'Unknown'}</TableCell>
                        <TableCell align="right">{provider?.stats?.total || 0}</TableCell>
                        <TableCell align="right">{provider?.stats?.successful || 0}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${provider?.stats?.successRate || 0}%`}
                            color={parseFloat(provider?.stats?.successRate || 0) > 80 ? 'success' : 
                                   parseFloat(provider?.stats?.successRate || 0) > 60 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{provider?.stats?.quotes || 0}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${provider?.stats?.quoteRate || 0}%`}
                            color={parseFloat(provider?.stats?.quoteRate || 0) > 30 ? 'success' : 
                                   parseFloat(provider?.stats?.quoteRate || 0) > 15 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{provider?.stats?.sales || 0}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${provider?.stats?.conversionRate || 0}%`}
                            color={parseFloat(provider?.stats?.conversionRate || 0) > 20 ? 'success' : 
                                   parseFloat(provider?.stats?.conversionRate || 0) > 10 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {/* Daily Trends */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Daily Submission Trends
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analytics.trends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="submissions" stroke="#8884d8" name="Total Submissions" />
                  <Line type="monotone" dataKey="successful" stroke="#82ca9d" name="Successful" />
                  <Line type="monotone" dataKey="quotes" stroke="#ffc658" name="Quotes" />
                  <Line type="monotone" dataKey="sales" stroke="#ff7300" name="Sales" />
                  <Line type="monotone" dataKey="opened" stroke="#cccccc" name="OPENED" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AgentDashboard;